// 知识库数据集列表页面
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogDescription,
} from '@you/ui'
import { ChevronLeft, Search, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useKnowledgeStore } from '@/stores/knowledgeStore'
import { Icon } from '@/components/Icon'
import { KnowledgeDocType } from '@/api/types/knowledge.types'
import {
  DatasetStatus,
  type Dataset as DatasetType,
} from '@/api/types/dataset.type'
import { useState, useCallback, useMemo, useEffect } from 'react'
import { useDebounce } from '@/hooks/useDebounce'
import DatasetList from '@/components/Knowledge/DatasetList'
import { useDataset } from '@/api/hooks/useDataset'
import { toast } from '@you/ui'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import AddDatasetDialog, {
  type DatasetForm,
} from '@/components/Knowledge/UpdateDatasetDialog'
import EmptyState from '@/components/common/EmptyState'

/**
 * 状态筛选选项
 */
const STATUS_OPTIONS = [
  { value: 'all', label: '全部状态' },
  { value: DatasetStatus.ENABLED.toString(), label: '启用' },
  { value: DatasetStatus.DISABLED.toString(), label: '停用' },
] as const

/**
 * 文档类型标签配置
 */
const DOC_TYPE_CONFIG = {
  [KnowledgeDocType.TABLE]: {
    icon: 'tableSmall',
    text: '表格类型',
    background:
      'linear-gradient(270deg, rgba(82, 157, 255, 0.1) 0%, rgba(71, 255, 163, 0.1) 100%)',
    textGradient: 'linear-gradient(180deg, #3C7ACA 0%, #5ABE8C 100%)',
    iconColor: 'linear-gradient(180deg, #3C7ACA 0%, #5ABE8C 100%)',
  },
  [KnowledgeDocType.TEXT]: {
    icon: 'textSmall',
    text: '文本类型',
    background:
      'linear-gradient(270deg, rgba(135, 82, 255, 0.1) 0%, rgba(71, 151, 255, 0.1) 100%)',
    textGradient: 'linear-gradient(180deg, #8752FF 0%, #4797FF 100%)',
    iconColor: 'linear-gradient(180deg, #8752FF 0%, #4797FF 100%)',
  },
} as const

/**
 * 文档类型标签组件
 */
const DocTypeTag = ({ docType }: { docType: KnowledgeDocType }) => {
  const config = DOC_TYPE_CONFIG[docType]

  return (
    <div
      className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium"
      style={{
        background: config.background,
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      {/* 图标 */}
      <div
        className="flex items-center justify-center"
        style={{
          background: config.iconColor,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        <Icon name={config.icon} size={14} />
      </div>

      {/* 文字 */}
      <span
        style={{
          background: config.textGradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {config.text}
      </span>
    </div>
  )
}

const Dataset = () => {
  const navigate = useNavigate()
  const { currentKnowledge, setCurrentDataset } = useKnowledgeStore()

  // 状态管理
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [keyword, setKeyword] = useState<string>('')
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false)
  const [pendingDatasetId, setPendingDatasetId] = useState<string | null>(null)

  // 构建请求参数
  const requestParams = useMemo(
    () => ({
      knowledgeBaseId: currentKnowledge?.id || '',
      search: keyword || undefined,
      status:
        selectedStatus === 'all'
          ? undefined
          : (Number(selectedStatus) as DatasetStatus),
    }),
    [currentKnowledge?.id, keyword, selectedStatus]
  )

  // 使用数据集 hooks
  const {
    datasetList,
    removeDataset,
    isDeleting,
    toggleDatasetStatus,
    isToggling,
    saveOrUpdateDataset,
    isUpdating,
  } = useDataset(requestParams)

  // 防抖搜索
  const debouncedSetSearchKeyword = useDebounce((value: string) => {
    setKeyword(value)
  }, 300)

  /**
   * 处理返回操作
   */
  const handleBack = useCallback(() => {
    navigate('/knowledge')
  }, [navigate])

  /**
   * 处理状态筛选
   */
  const handleStatusFilterChange = useCallback((value: string) => {
    setSelectedStatus(value)
  }, [])

  /**
   * 处理搜索
   */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      debouncedSetSearchKeyword(value)
    },
    [debouncedSetSearchKeyword]
  )

  /**
   * 处理新建数据集
   */
  const handleCreateNew = useCallback(() => {
    setShowCreateDialog(true)
  }, [])

  /**
   * 处理编辑数据集
   */
  const handleEdit = useCallback(
    (dataset: DatasetType) => {
      setCurrentDataset(dataset)
      navigate('/knowledge/dataset-import')
    },
    [navigate, setCurrentDataset]
  )

  /**
   * 处理删除数据集
   */
  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await removeDataset(id)
        toast.success('数据集删除成功')
        setShowDeleteDialog(null)
      } catch (error) {
        toast.error('删除失败')
        console.error('删除失败:', error)
      }
    },
    [removeDataset]
  )

  /**
   * 处理状态切换
   */
  const handleStatusToggle = useCallback(
    async (id: string, checked: boolean) => {
      try {
        // checked 是 Switch 的新状态，直接转换为数字状态
        const newStatus = checked ? 1 : 0
        await toggleDatasetStatus(id, newStatus)
        toast.success(checked ? '数据集已启用' : '数据集已停用')
      } catch (error) {
        toast.error('状态切换失败')
        console.error('状态切换失败:', error)
      }
    },
    [toggleDatasetStatus]
  )

  /**
   * 处理创建数据集
   */
  const handleCreateDataset = useCallback(
    async (formData: DatasetForm) => {
      try {
        const datasetData = {
          ...formData,
          knowledgeBaseId: currentKnowledge?.id || '',
          workspaceId: useWorkspaceStore.getState().currentWorkspace?.id || '',
        }

        await saveOrUpdateDataset(datasetData)
        toast.success('数据集创建成功')
      } catch (error) {
        toast.error('创建失败')
        throw error // 重新抛出错误，让组件处理
      }
    },
    [currentKnowledge, saveOrUpdateDataset]
  )

  /**
   * 处理创建并导入数据集
   */
  const handleCreateAndImport = useCallback(
    async (formData: DatasetForm) => {
      try {
        const datasetData = {
          ...formData,
          knowledgeBaseId: currentKnowledge?.id || '',
          workspaceId: useWorkspaceStore.getState().currentWorkspace?.id || '',
        }

        const result = await saveOrUpdateDataset(datasetData)
        toast.success('数据集创建成功')

        // 设置待处理的数据集ID，等待列表更新后再跳转
        setPendingDatasetId(result.id)
      } catch (error) {
        toast.error('创建失败')
        throw error // 重新抛出错误，让组件处理
      }
    },
    [currentKnowledge, saveOrUpdateDataset, setCurrentDataset, navigate]
  )

  // 监听待处理的数据集ID，当列表更新后自动跳转
  useEffect(() => {
    if (pendingDatasetId && datasetList.length > 0) {
      const createdDataset = datasetList.find(
        (dataset) => dataset.id === pendingDatasetId
      )
      if (createdDataset) {
        setCurrentDataset(createdDataset)
        navigate('/knowledge/dataset-import')
        setPendingDatasetId(null) // 清除待处理的ID
      }
    }
  }, [pendingDatasetId, datasetList, setCurrentDataset, navigate])

  // 转换 API 数据为 UI 数据格式
  const transformedDatasets = useMemo(
    () =>
      datasetList.map((dataset) => ({
        ...dataset,
        status: dataset.status,
        filesNum: dataset.filesNum || 0,
        updateTime: dataset.updateTime || new Date().toISOString(),
      })),
    [datasetList]
  ) as DatasetType[]

  // 如果没有当前知识库，显示错误
  if (!currentKnowledge?.id) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">请先选择知识库</div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* 头部导航区域 */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        {/* 左侧：返回按钮和知识库信息 */}
        <div className="flex items-center space-x-4">
          {/* 返回按钮 */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="flex items-center space-x-2 p-0 h-auto"
          >
            <ChevronLeft className="h-6 w-6 text-black" />
          </Button>

          {/* 知识库名称和类型标签 */}
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-semibold text-black">
              {currentKnowledge?.name || '知识库数据集'}
            </h1>

            {/* 文档类型标签 - 根据当前知识库的实际类型显示 */}
            {currentKnowledge && currentKnowledge.docType && (
              <DocTypeTag docType={currentKnowledge.docType} />
            )}
          </div>
        </div>
      </div>

      {/* 顶部操作栏 */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        {/* 左侧：状态筛选 */}
        <div className="flex items-center space-x-4">
          <Select
            value={selectedStatus}
            onValueChange={handleStatusFilterChange}
          >
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="选择状态" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 右侧：搜索和新建 */}
        <div className="flex items-center space-x-3">
          {/* 搜索框 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="搜索数据集名称或描述"
              defaultValue={keyword}
              onChange={handleSearchChange}
              className="px-0 w-[300px] pl-9 pr-2"
            />
          </div>

          {/* 新建按钮 */}
          <Button
            onClick={handleCreateNew}
            className="flex items-center space-x-2 w-[160px] gradient-primary"
          >
            <Plus className="h-4 w-4" />
            <span>新建</span>
          </Button>
        </div>
      </div>

      {/* 数据集内容区域 - 自适应高度 */}
      <div className="flex-1 overflow-hidden">
        {transformedDatasets.length > 0 ? (
          <DatasetList
            data={transformedDatasets}
            onEdit={handleEdit}
            onDelete={(id) => setShowDeleteDialog(id)}
            onStatusChange={handleStatusToggle}
            isLoading={false}
            isToggling={isToggling}
          />
        ) : (
          <EmptyState description="暂无数据集" />
        )}
      </div>

      {/* 添加数据集对话框 */}
      <AddDatasetDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateDataset}
        onCreateAndImport={handleCreateAndImport}
        isSubmitting={isUpdating}
      />

      {/* 删除确认弹窗 */}
      <Dialog
        open={!!showDeleteDialog}
        onOpenChange={() => setShowDeleteDialog(null)}
      >
        <DialogContent className="w-[580px]">
          <DialogHeader>
            <div className="flex items-center space-x-2">
              <Icon className="text-[#FF7D00]" name="warning" size={24} />
              <span className="text-[20px]">删除数据集</span>
            </div>
          </DialogHeader>
          <DialogDescription className="text-center text-[#666666] text-lg py-10">
            数据集删除后无法恢复，请确认是否删除？
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteDialog(null)}
              disabled={isDeleting}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={() => showDeleteDialog && handleDelete(showDeleteDialog)}
              disabled={isDeleting}
            >
              {isDeleting ? '删除中...' : '确认'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Dataset
